import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { authToken } from '../../scripts/authentication.script';
import { fetchExportBlob, BlobExportResult } from '../../scripts/export.blob.script';

/**
 * Guard: rejects falsy or stringified-undefined path segments before they
 * reach axios.  Every line-level route interpolates `id` (statement) and
 * `line_id` into the URL — if either is missing the request would hit a
 * wrong endpoint or produce a 404 with `undefined` in the path.
 *
 * Returns `true` when ALL segments are truthy strings; `false` otherwise.
 * Callers must bail out (and surface a user-visible error) when `false`.
 */
function requireIds(...segments: unknown[]): boolean {
  for (const s of segments) {
    if (!s || typeof s !== 'string' || s === 'undefined' || s === 'null') {
      console.error('[BankStatementDA] refusing to emit request — falsy id detected', segments)
      return false
    }
  }
  return true
}

/** Sentinel response returned when a required id is missing. */
const FALSY_ID_RESPONSE: ApiResponse = {
  status: 0,
  message: 'Missing id — request blocked',
  data: { code: 'FALSY_ID', message: 'A required identifier is missing. Please reload the page.' },
}

export class BankStatementDA {
  static apiResponse: ApiResponse

  public static async getStatements(
    id_company: string,
    params: {
      id_treasury_account?: string
      page?: number
      limit?: number
      sort_field?: string
      sort_mode?: string
      sort_order?: 'asc' | 'desc'
    } = {}
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements`

    let config: any = {
      headers: { Authorization: authToken() },
      params: {
        ...(params.id_treasury_account && { id_treasury_account: params.id_treasury_account }),
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        ...(params.sort_field && { sort_field: params.sort_field }),
        ...(params.sort_mode && { sort_mode: params.sort_mode }),
        ...(params.sort_order && { sort_order: params.sort_order }),
      },
    }

    await axios.get(url, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async getStatement(id_company: string, id: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.get(url, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async importStatement(
    id_company: string,
    id_treasury_account: string,
    file: File,
    format: 'CAMT053' | 'CAMT054' | 'CSV'
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/import`

    let formData = new FormData()
    formData.append('file', file)
    formData.append('id_treasury_account', id_treasury_account)
    formData.append('format', format)

    let config: any = {
      headers: {
        Authorization: authToken(),
        'Content-Type': 'multipart/form-data',
      },
    }

    await axios.post(url, formData, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async confirmStatement(
    id_company: string,
    id_bank_statement: string,
    lines?: any[],
    opening_balance?: number,
    closing_balance?: number
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id_bank_statement}/confirm`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    const body: any = {}
    if (lines) body.lines = lines
    if (opening_balance !== undefined) body.opening_balance = opening_balance
    if (closing_balance !== undefined) body.closing_balance = closing_balance

    await axios.post(url, body, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async createFromDocument(
    id_company: string,
    id_treasury_account: string,
    id_document_inbox: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/from-document`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, { id_treasury_account, id_document_inbox }, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async deleteStatement(id_company: string, id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.delete(url, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async invalidateStatement(id_company: string, id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/invalidate`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.put(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async autoMatchAll(id_company: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/auto-match-all`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async getPaymentSuggestions(id_company: string, id: string, line_id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/payment-suggestions`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.get(url, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async linkExistingPayment(
    id_company: string,
    id: string,
    line_id: string,
    payload: { id_payment: string; kind: 'invoice' | 'provider-invoice'; id_invoice?: string; id_provider_invoice?: string; amount: number }
  ): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/link-payment`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, payload, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async getTransactions(
    id_company: string,
    params: {
      id_treasury_account?: string
      reconciliation_status?: string
      from?: string
      to?: string
      iban_counterpart?: string
      amount_min?: number
      amount_max?: number
      page?: number
      limit?: number
      sort_field?: string
      sort_mode?: string
    } = {}
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/transactions`

    let config: any = {
      headers: { Authorization: authToken() },
      params: {
        ...(params.id_treasury_account && { id_treasury_account: params.id_treasury_account }),
        ...(params.reconciliation_status && { reconciliation_status: params.reconciliation_status }),
        ...(params.from && { from: params.from }),
        ...(params.to && { to: params.to }),
        ...(params.iban_counterpart && { iban_counterpart: params.iban_counterpart }),
        ...(params.amount_min !== undefined && { amount_min: params.amount_min }),
        ...(params.amount_max !== undefined && { amount_max: params.amount_max }),
        page: params.page ?? 1,
        limit: params.limit ?? 100,
        ...(params.sort_field && { sort_field: params.sort_field }),
        ...(params.sort_mode && { sort_mode: params.sort_mode }),
      },
    }

    await axios.get(url, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async payAndLinkInvoice(
    id_company: string,
    id: string,
    line_id: string,
    payload: { id_provider_invoice?: string; id_invoice?: string; kind: 'invoice' | 'provider-invoice'; amount: number; date: string }
  ): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/pay-and-link`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, payload, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async linkExpenseEntry(
    id_company: string,
    id: string,
    line_id: string,
    payload: { id_expense: string }
  ): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/link-expense`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, payload, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async matchLine(id_company: string, id: string, line_id: string, id_entry: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id, id_entry)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/match`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.put(url, { id_entry }, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async unmatchLine(id_company: string, id: string, line_id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/unmatch`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.put(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async deleteReconciliationEntry(id_company: string, id: string, line_id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/delete-entry`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async ignoreLine(id_company: string, id: string, line_id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/ignore`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.put(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async saveDraft(
    id_company: string,
    id_bank_statement: string,
    lines?: any[],
    opening_balance?: number,
    closing_balance?: number
  ): Promise<ApiResponse> {
    if (!requireIds(id_company, id_bank_statement)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id_bank_statement}/draft`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    const body: any = {}
    if (lines) body.lines = lines
    if (opening_balance !== undefined) body.opening_balance = opening_balance
    if (closing_balance !== undefined) body.closing_balance = closing_balance

    await axios.put(url, body, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async restoreLine(id_company: string, id: string, line_id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/restore`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.put(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async exportTransactions(
    id_company: string,
    format: 'xlsx' | 'pdf',
    params: {
      id_treasury_account?: string
      reconciliation_status?: string
      from?: string
      to?: string
      q?: string
      sort_field?: string
      sort_mode?: string
      lang?: string
    } = {},
    fallbackName?: string
  ): Promise<BlobExportResult> {
    const url = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/transactions/export`
    return fetchExportBlob(url, {
      format,
      id_treasury_account: params.id_treasury_account,
      reconciliation_status: params.reconciliation_status,
      from: params.from,
      to: params.to,
      q: params.q,
      sort_field: params.sort_field,
      sort_mode: params.sort_mode,
      lang: params.lang,
    }, fallbackName)
  }

  public static async getLineCandidates(id_company: string, id: string, line_id: string): Promise<ApiResponse> {
    if (!requireIds(id_company, id, line_id)) return FALSY_ID_RESPONSE
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/lines/${line_id}/candidates`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.get(url, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async retryExtraction(id_company: string, id: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/retry-extraction`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }

  public static async triggerAutoMatch(id_company: string, id: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/bank-statements/${id}/auto-match`

    let config: any = {
      headers: { Authorization: authToken() },
    }

    await axios.post(url, {}, config)
      .then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data,
        }
      })
      .catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message },
        }
      })

    return this.apiResponse
  }
}
